/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/Events',
	'../../../app/nls/StringResources',
	'underscore',
	'../../../lib/@waca/core-client/js/core-client/i18n/Formatter',
	'moment-timezone'
], function (Events, StringResources, _, Formatter, moment) {
	class DashboardPinningService extends Events {
		constructor() {
			super(arguments);

			this.PIN_VERSION = '3';
			this.PIN_URL = 'v1/users/~/pins';
			this._pinIdsToUpgradeInCM = [];
			this._timeframePinBuckets = null;
			this._timeframePinBucketNames = ['all', 'today', 'yesterday', 'pastWeek', 'pastMonth', 'earlier'];
			this._pinIdMap = {};
		}

		initialize({ appController }) {
			this._glassContext = appController.glassContext;
			return this._glassContext.getSvc('.UpgradeService').then( upgrades => {
				this._latestDashboardSpecVersion = upgrades.getLatestDashboardSpecVersion();

				// Do some work ahead of time to figure out our time buckets
				const userProfileService = this._glassContext.getCoreSvc('.UserProfile');
				this._timezone = userProfileService.preferences.timeZoneID || 'America/New_York';
				this._today = moment().tz(this._timezone).startOf('day');
				this._yesterday = this._today.clone().subtract(1, 'days');
				this._pastWeek = this._today.clone().subtract(7, 'days');
				this._pastMonth = this._today.clone().subtract(31, 'days');
			});
		}

		/**
		 * Used by search UI to get the cached pins.  This UI only renders after we've queried for the pins,
		 * so we return without a promise here which is what the search logic needs.
		 * @param {String} timeframe 
		 */
		getCachedPins(timeframe = 'all') {
			return this._timeframePinBuckets[timeframe] || [];
		}

		/**
		 * Get the list of pins from the server
		 * @param {String} timeframe - Must be one of the following 'all', 'today', 'yesterday', 'pastWeek', 'pastMonth', 'earlier'
		 */
		getPins(timeframe = 'all') {
			if (this._timeframePinBuckets) {
				return Promise.resolve(this._timeframePinBuckets[timeframe]);
			}

			return this._glassContext.getCoreSvc('.Ajax').ajax({
				url: this.PIN_URL,
				type: 'GET',
				contentType: 'application/json; charset=utf-8',
				dataType: 'json'
			})
				.then((response = {}) => {
					this._timeframePinBuckets = { all: [], today: [], yesterday: [], pastWeek: [], pastMonth: [], earlier: [] };

					// Upgrade the pins locally (on the client) before returning them.  We'll update the pins on the server lazilly
					return this._upgradePinsLocally(response.data);
				})
				.then((upgradedPins) => {
					const pins = this._sortPins(upgradedPins);
					this._prepPinsForUI(pins);
					this._cachePinsInAppropriateTimeFrameBuckets(pins);

					// No need to wait for this to finish before the UI can render
					this._slowlySaveUpgradedPinsInCM();

					return this._timeframePinBuckets[timeframe];
				})
				.catch((result) => {
					this._glassContext.getCoreSvc('.Logger').error('Failed to get pins', result);

					const message = this._retrieveErrorMessage({
						result: result,
						defaultMessage: StringResources.get('pinRetrieveError')
					});
					this._glassContext.appController.showErrorMessage(message, StringResources.get('pinErrorTitle'));

					return [];
				});
		}

		/**
		 * Get the pin for the given ID
		 * @param {String} pinId 
		 */
		getPin(pinId) {
			if (this._pinIdMap[pinId]) {
				return Promise.resolve(this._pinIdMap[pinId]);
			}

			// We should never ask for a specific pin before querying all of them.. safe guard in case we do for some odd reason.
			return this.getPins()
				.then((pins) => {
					return _.find(pins, (pin) => {
						return pin.id === pinId;
					});
				});
		}

		/**
		 * Saves a pin in CM
		 * @param {Object} pin Pin object to save
		 */
		addPin(pin) {
			pin.version = this.PIN_VERSION;
			const pinJSON = JSON.stringify(pin);

			return this._glassContext.getCoreSvc('.Ajax').ajax({
				type: 'POST',
				url: this.PIN_URL,
				contentType: 'application/json; charset=utf-8',
				dataType: 'json',
				data: pinJSON
			})
				.then((result = {}) => {
					const newPin = result.data;
					this._prepPinsForUI([newPin]);
					this._addPinToCache(newPin, ['all', 'today', 'pastWeek', 'pastMonth'], true);

					this.trigger('pin:created', newPin);
					return newPin;
				})
				.catch((result) => {
					const message = this._retrieveErrorMessage({
						result: result,
						defaultMessage: StringResources.get('contentPinFailOld')
					});
					const error = new Error(message);
					this._glassContext.getCoreSvc('.Logger').error('addPin error', error);
					throw error;
				});
		}

		/**
		 * Removes the pin locally and shows a toast.  Once the toast goes away (can no longer undo)
		 * we complete the delete by deleting it from CM
		 * @param {String} pinId 
		 */
		deletePin(pinId, undoDeletionCallback) {
			return new Promise((resolve, reject) => {
				try {
					if (this._glassContext.getCoreSvc('.UserProfile').preferences.accessibilityFeatures) {
						this._glassContext.appController.showMessage(
							StringResources.get('deletePinConfirmationMessage'),
							StringResources.get('deletePinConfirm'),
							'info',
							['ok', 'cancel'],
							undefined,
							(evt) => {
								if (evt.btn === 'ok') {
									this._removePinFromCache(pinId);
									this._completePinDelete(pinId);
								}
								resolve();
							});

					} else {
						const removedInfo = this._removePinFromCache(pinId);
						if (!removedInfo.removedPin) {
							this._glassContext.getCoreSvc('.Logger').error('Trying to remove a pin that is not in our array', pinId, this);
						}
						let undidDelete = false;
						this._glassContext.appController.showToast(
							StringResources.get('pinDeletedToast'),
							{
								type: 'info',
								btnLabel: StringResources.get('undo'),
								callback: () => {
									undidDelete = true;
									this._addPinToCache(removedInfo.removedPin, removedInfo.timeframeBucketNames, false, true);
									undoDeletionCallback(pinId);
								},
								newestOnTop: true,
								preventDuplicates: false,
								timeOut: 6000,
								extendedTimeOut: 1000,
								onHidden: () => {
									if (!undidDelete) {
										this._completePinDelete(pinId);
									}
									resolve(undidDelete);
								}
							}
						);
					}
				} catch (error) {
					reject(error);
				}
			});
		}

		/**
		 * The toast message is gone so it's safe to delete the pin from CM
		 */
		_completePinDelete(pinId) {
			// If the pin we've just deleted is still in the queue to update in CM then remove it from the queue
			this._pinIdsToUpgradeInCM = this._pinIdsToUpgradeInCM.filter((id) => {
				return id !== pinId;
			});

			return this._glassContext.getCoreSvc('.Ajax').ajax({
				type: 'DELETE',
				url: `${this.PIN_URL}/${pinId}`,
				contentType: 'application/json; charset=utf-8',
				dataType: 'json'
			})
				.then((result = {}) => {
					return result;
				})
				.catch((result) => {
					const message = this._retrieveErrorMessage({
						result: result,
						defaultMessage: StringResources.get('pinDeleteErrorOld')
					});
					const error = new Error(message);
					this._glassContext.appController.showErrorMessage(message, StringResources.get('pinErrorTitle'));

					this._glassContext.getCoreSvc('.Logger').error('deletePin error', error);
					throw error;
				});
		}

		/**
		 * Go through the upgraded pins one by one and push the upgraded spec to CM.  Only
		 * send one PUT request at a time to not hurt performance for the user.
		 */
		_slowlySaveUpgradedPinsInCM() {
			const pinId = this._pinIdsToUpgradeInCM.shift();
			if (pinId) {
				return this.getPin(pinId)
					.then((pin) => {
						this._glassContext.getCoreSvc('.Logger').info('Updating upgraded pin spec in CM', pin);
						return this._glassContext.getCoreSvc('.Ajax').ajax({
							type: 'PUT',
							url: `${this.PIN_URL}/${pinId}`,
							contentType: 'application/json; charset=utf-8',
							dataType: 'json',
							data: JSON.stringify(pin)
						})
							.then(() => {
								return this._slowlySaveUpgradedPinsInCM();
							})
							.catch((error) => {
								// Stop trying to save the upgrades on the first error.  Possible timeout or logged off.
								this._glassContext.getCoreSvc('.Logger').error('Pin upgrade failed to save in CM.', error);
							});
					});
			}

			return Promise.resolve();
		}

		/**
		 * Removes a pin from our caches
		 * @param {String} pinId 
		 */
		_removePinFromCache(pinId) {
			if (this._pinIdMap) {
				delete this._pinIdMap[pinId];
			}

			// Need to keep track of the timeframe buckets this pin was in case the user undoes the delete
			const timeframeBucketNames = [];
			let removedPin = null;

			if (this._timeframePinBuckets) {
				const filterOutPin = function (pin, timeframeBucketName) {
					if (pin.id === pinId) {
						removedPin = pin;
						timeframeBucketNames.push(timeframeBucketName);
						return false;
					}

					return true;
				};

				this._timeframePinBucketNames.forEach((bucketName) => {
					this._timeframePinBuckets[bucketName] = this._timeframePinBuckets[bucketName].filter((pin) => {
						return filterOutPin(pin, bucketName);
					});
				});
			}

			return {
				removedPin: removedPin,
				timeframeBucketNames: timeframeBucketNames
			};
		}

		/**
		 * Adds a pin to our caches
		 * @param {Object} pin - the pin to add
		 * @param {[String]} buckets - the list of buckets to add the pin to
		 * @param {Boolean} addToBeginning - Should the pin get added to the beginning of the list
		 * @param {Boolean} sortPins - should the timeframe bucket be sorted after the pin gets added.
		 */
		_addPinToCache(pin, buckets = this._timeframePinBucketNames, addToBeginning = false, sortPins = false) {
			// If we haven't queried for all the pins yet then no need to add the pin.  We'll grab them 
			// all when the pin panel is open.
			if (!this._timeframePinBuckets) {
				return;
			}

			this._pinIdMap[pin.id] = pin;

			buckets.forEach((bucket) => {
				if (addToBeginning) {
					this._timeframePinBuckets[bucket].unshift(pin);
				} else {
					this._timeframePinBuckets[bucket].push(pin);
				}

				// Currently only true when we undo a delete.  We need to resort the buckets once the pin is placed back
				if (sortPins) {
					this._sortPins(this._timeframePinBuckets[bucket]);
				}
			});
		}

		_retrieveErrorMessage(input) {
			input = input || {};
			let message = input.defaultMessage;
			try {
				const text = JSON.parse(input.result.responseText);
				message = text.error;
			} catch(e) {
				// ignore JSON parsing errors
			}
			return message;
		}

		/**
		 * Convert properties from the pin object to the required values for the UI
		 */
		_prepPinsForUI(pins) {
			const dashboardApi = this._getCurrentContentViewDashboardApi();
			const smartNamingService = dashboardApi.getDashboardCoreSvc('.SmartNamingSvc');

			pins.forEach((pin) => {
				// process pin date
				if (pin.timestamp) {
					pin.pinAge = Formatter.formatDateTime(pin.timestamp, {
						type: 'datetime',
						formatLength: 'short',
						timezone: this._timezone
					});
				}
				// smart name
				pin.displayName = smartNamingService.getPinName(pin);

				// check for pin image
				if (!pin.thumbUri) {
					pin.defaultImage = 'wfg_pin';
				}
			});
		}

		/**
		 * Caches the pins into their timeframe buckets
		 */
		_cachePinsInAppropriateTimeFrameBuckets(pins) {
			pins.forEach((pin) => {
				const timestamp = moment.tz(pin.timestamp, this._timezone);
				this._timeframePinBuckets.all.push(pin);
				let timeframeBucketNames = null;

				if (timestamp.isSame(this._today, 'day')) {
					timeframeBucketNames = ['today', 'pastWeek', 'pastMonth'];
				} else if (timestamp.isSame(this._yesterday, 'day')) {
					timeframeBucketNames = ['yesterday', 'pastWeek', 'pastMonth'];
				} else if (timestamp.isAfter(this._pastWeek)) {
					timeframeBucketNames = ['pastWeek', 'pastMonth'];
				} else if (timestamp.isAfter(this._pastMonth)) {
					timeframeBucketNames = ['pastMonth'];
				} else {
					timeframeBucketNames = ['earlier'];
				}

				this._addPinToCache(pin, timeframeBucketNames);
			});
		}

		/**
		 * Sorts the given array of pins by modified date, with the newest first in the list.
		 */
		_sortPins(pins) {
			return pins.sort(function (a, b) {
				const aTime = a.timestamp;
				const bTime = b.timestamp;
				return ((aTime > bTime) ? -1 : ((aTime < bTime) ? 1 : 0));
			});
		}

		/**
		 * Upgrade the pin specs but do not push the upgraded specs to CM yet.  We'll do that slowly once we render the Pins panel
		 * @param {*} pins 
		 */
		_upgradePinsLocally(pins = []) {
			//const upgradePromises = [];
			const upgradeOptions = {
				ajaxSvc: this._glassContext.getCoreSvc('.Ajax'),
				services: this._getCurrentContentViewServices(),
				logger: this._glassContext.getCoreSvc('.Logger'),
				pinUpgrade: true,
				glassContext: this._glassContext,
				dashboardApi: this._getCurrentContentViewDashboardApi(),
				showErrorToast: false
			};
			const pinsToUpgrade = [];
			return this._glassContext.getSvc('.UpgradeService')
				.then((upgradeService) => {
					pins.forEach((pin) => {
						if (pin.content && pin.content.specVersion !== this._latestDashboardSpecVersion) {
							pinsToUpgrade.push(pin);
						}
					});

					if (pinsToUpgrade.length > 0) {
						console.log(pinsToUpgrade.length + ' pins to upgrade.');
					}

					/**
					 * Our upgrade service uses static upgrade classes and some of those keep state during the upgrade process.
					 * For this reason we have to run the upgrades in sequence instead of in parallel.  Use the reduce method
					 * to programatically chain promises
					 */
					return new Promise((resolve) => {
						const upgradePromise = pinsToUpgrade.reduce((currentPromise, pin) => {
							return currentPromise
								.then(() => {
									try {
										return this._upgradePinSpec(pin, upgradeService, upgradeOptions)
											.then(() => {
												this._pinIdsToUpgradeInCM.push(pin.id);
											}).catch((error) => {
												// if one pin fails, omit it from the list and move on
												this._glassContext.getCoreSvc('.Logger').error('Unable to upgrade pin:', pin, 'Failed with error: ', error);
											});
									} catch (error) {
										// if one pin fails, omit it from the list and move on
										this._glassContext.getCoreSvc('.Logger').error('Unable to upgrade pin:', pin, 'Failed with error: ', error);
									}
								});
						}, Promise.resolve([]));

						upgradePromise
							.then(() => {
								resolve(pins);
							});
					});
				});
		}

		_upgradePinSpec(pinSpec, upgradeService, upgradeOptions) {
			// assuming the board spec version is 6 if there is no version attribute
			const boardSpec = {
				version: pinSpec.version ? pinSpec.content.specVersion: 6,
				layout: pinSpec.content.layout,
				widgets: this._getObjectFromArrayById(pinSpec.content.widgets)
			};

			// Old pin specs used datasetShapings while every other part of the code uses datasetShaping
			if(pinSpec.content.datasetShapings) {
				boardSpec.datasetShaping = this._getObjectFromArrayById(pinSpec.content.datasetShapings);
			} else if (pinSpec.content.datasetShaping) {
				boardSpec.datasetShaping = this._getObjectFromArrayById(pinSpec.content.datasetShaping);
			}

			if(pinSpec.content.dataSources) {
				boardSpec.dataSources = pinSpec.content.dataSources;
			}

			if (pinSpec.content.episodes) {
				boardSpec.timeline = {
					episodes: this._getObjectFromArrayById(pinSpec.content.episodes)
				};
			}

			// save the original version
			const upgrades = pinSpec.content.upgrades || [];
			const original = boardSpec.version;

			if(upgrades.indexOf(original) === -1) {
				upgrades.push(original);
				pinSpec.content.upgrades = upgrades;
			} else {
				this._glassContext.getCoreSvc('.Logger').warn('Pin spec with id "' + pinSpec.id + '" and board spec version "'+pinSpec.content.specVersion+ '" being upgraded to an previously upgraded spec version: ', original);
			}

			return upgradeService.upgrade(boardSpec, null /*means latest*/, upgradeOptions)
				.then((upgradedBoardSpec) => {
					// TODO the logic to create a pin spec from a board spec should be put in one place
					// currently it is here and in PinAction. See Work Item 187395
					const upgradedPinContent = {
						layout: upgradedBoardSpec.layout,
						widgets: _.toArray(upgradedBoardSpec.widgets),
						specVersion: upgradedBoardSpec.version,
						upgrades: upgrades
					};
					if(upgradedBoardSpec.dataSources) {
						upgradedPinContent.dataSources = upgradedBoardSpec.dataSources;
					}

					if(pinSpec.content.episodes){
						upgradedPinContent.episodes = _.toArray(upgradedBoardSpec.timeline.episodes);
					}
					// if for some reason the spec did not upgrade fully it may still
					// contain datasetShaping
					if(upgradedBoardSpec.datasetShaping) {
						upgradedPinContent.datasetShaping = _.toArray(upgradedBoardSpec.datasetShaping);
					}
					pinSpec.content = upgradedPinContent;

					// we should be the newest pin version now
					pinSpec.version = this.PIN_VERSION;

					return pinSpec;
				})
				.catch((error) => {
					// IF we have an error.obj then the upgrade had some succefull steps, return that spec
					if (error.obj) {
						this._glassContext.getCoreSvc('.Logger').warn('Pin spec did not fully upgrade', error, pinSpec);
						return error.obj;
					}

					throw error;
				});
		}

		_getObjectFromArrayById(array) {
			return _.object(_.map(array, function (item) {
				return [item.id, item];
			}));
		}

		/**
		 * Yuck yuck yuck.. look away.  This is really only needed since the pin service (glass service) needs access to 
		 * some dashboard specific services when querying/adding pins.  In the long run a lot of these services should
		 * be moved to glass services since they can be shared accross dashboards.
		 */
		_getCurrentContentViewDashboardApi() {
			try {
				return this._glassContext.appController.currentAppView.currentContentView.getDashboardApi();
			} catch (e) {
				this._glassContext.getCoreSvc('.Logger').error('Trying to use the pinning service outside of a dashboard.', e);
			}
		}

		/**
		 * Yuck yuck yuck.. look away.  This is really only needed since the pin service (glass service) needs access to 
		 * some dashboard specific services when querying/adding pins.  In the long run a lot of these services should
		 * be moved to glass services since they can be shared accross dashboards.
		 */
		_getCurrentContentViewServices() {
			try {
				return this._glassContext.appController.currentAppView.currentContentView.services;
			} catch (e) {
				this._glassContext.getCoreSvc('.Logger').error('Trying to use the pinning service outside of a dashboard.', e);
			}
		}
	}

	return DashboardPinningService;
});